import React from "react";
import { Link } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import ListingGrid from "@/components/listings/ListingGrid";
import { Heart, Loader2 } from "lucide-react";

export default function MyFavoritesPage() {
  const { listings, isLoading, isError, refetch } = useFavorites();

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="My favourites" description="Listings you have saved on Allsale." />
      <Breadcrumbs items={[{ label: "My favourites" }]} />
      <div className="mt-6">
        <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">My favourites</h1>
        <p className="mt-2 text-slate-500">Everything you have saved — synced across your devices.</p>
      </div>

      {isLoading && (
        <div className="mt-10 flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!isLoading && isError && (
        <div className="mt-10 rounded-2xl bg-white shadow-card p-10 text-center" data-testid="my-favorites-error">
          <Heart className="w-8 h-8 mx-auto text-red-400" />
          <h2 className="mt-3 font-display text-lg font-bold text-slate-900">
            Couldn&apos;t load your favourites
          </h2>
          <button
            type="button"
            onClick={() => refetch()}
            data-testid="my-favorites-retry"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-semibold px-5 py-2 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && listings.length === 0 && (
        <div className="mt-10 rounded-2xl bg-white shadow-card p-12 text-center" data-testid="my-favorites-empty">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-50 text-red-500 grid place-items-center">
            <Heart className="w-6 h-6" />
          </div>
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">No favourites yet</h2>
          <p className="mt-2 text-slate-500 max-w-md mx-auto">
            Tap the heart on any listing to save it here for later.
          </p>
          <Link
            to="/"
            data-testid="my-favorites-browse"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
          >
            Browse listings
          </Link>
        </div>
      )}

      {!isLoading && !isError && listings.length > 0 && (
        <div className="mt-8" data-testid="my-favorites-list">
          <ListingGrid listings={listings} />
        </div>
      )}
    </div>
  );
}
